import { getHotelsBounds } from './mapBounds';
import { fromPixelPoint, toPixelPoint } from './mercator';
import { CLUSTER_GRID_SIZE_PX, CLUSTER_MARGIN_PX, CLUSTER_MIN_SIZE, MAP_MAX_ZOOM, MAP_MIN_ZOOM } from '../config/map';
import type { MapBounds, MapCluster, MapCoords, MapHotel } from '../model/types';

export interface ClusterHotelsOptions {
  zoom: number;
  gridSize?: number;
  minClusterSize?: number;
  margin?: number;
}

interface GridCell {
  column: number;
  row: number;
  hotels: MapHotel[];
  sumX: number;
  sumY: number;
}

export function getClusterZoomKey(zoom: number): number {
  return Math.round(Math.min(Math.max(zoom, MAP_MIN_ZOOM), MAP_MAX_ZOOM));
}

export function clusterHotels(hotels: readonly MapHotel[], options: ClusterHotelsOptions): MapCluster[] {
  const gridSize = options.gridSize ?? CLUSTER_GRID_SIZE_PX;
  const minClusterSize = options.minClusterSize ?? CLUSTER_MIN_SIZE;
  const margin = options.margin ?? CLUSTER_MARGIN_PX;

  return [...toGridCells(hotels, options.zoom, gridSize).values()]
    .flatMap((cell) =>
      cell.hotels.length >= minClusterSize
        ? [toCluster(cell.hotels, getCellClusterAnchor(cell, { gridSize, margin }, options.zoom))]
        : cell.hotels.map((hotel) => toCluster([hotel], hotel.coords)),
    )
    .sort((a, b) => a.key - b.key);
}

function toGridCells(hotels: readonly MapHotel[], zoom: number, gridSize: number): Map<string, GridCell> {
  const cells = new Map<string, GridCell>();

  hotels.forEach((hotel) => {
    const point = toPixelPoint(hotel.coords, zoom);
    const column = Math.floor(point.x / gridSize);
    const row = Math.floor(point.y / gridSize);
    const key = `${column}|${row}`;
    const cell = cells.get(key) ?? { column, row, hotels: [], sumX: 0, sumY: 0 };

    cell.hotels.push(hotel);
    cell.sumX += point.x;
    cell.sumY += point.y;
    cells.set(key, cell);
  });

  return cells;
}

function getCellClusterAnchor(cell: GridCell, grid: { gridSize: number; margin: number }, zoom: number): MapCoords {
  const { gridSize, margin } = grid;
  const half = gridSize / 2;
  const inset = half - Math.min(margin, half);
  const centerX = cell.column * gridSize + half;
  const centerY = cell.row * gridSize + half;

  return fromPixelPoint(
    {
      x: clamp(cell.sumX / cell.hotels.length, centerX - inset, centerX + inset),
      y: clamp(cell.sumY / cell.hotels.length, centerY - inset, centerY + inset),
    },
    zoom,
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function toCluster(hotels: MapHotel[], anchor: MapCoords): MapCluster {
  const sorted = [...hotels].sort(compareHotels);

  return {
    key: hotels.reduce((min, hotel) => Math.min(min, hotel.hotelId), Infinity),
    anchor,
    hotels: sorted,
    minPrice: sorted[0].price,
    bounds: getHotelsBounds(hotels) ?? toPointBounds(anchor),
  };
}

function compareHotels(a: MapHotel, b: MapHotel): number {
  return comparePrices(a.price, b.price) || a.hotelId - b.hotelId;
}

function comparePrices(a: number | null, b: number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  return a - b;
}

function toPointBounds(coords: MapCoords): MapBounds {
  return {
    west: coords.longitude,
    south: coords.latitude,
    east: coords.longitude,
    north: coords.latitude,
  };
}
